import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';

import {
    useGetClubSettingsQuery,
    useUpdateClubLogoMutation,
    useUpdateClubVisibilityMutation,
    useUpdateClubBriefMutation,
    useCreateClubInviteMutation,
    useGetClubBillingQuery,
    useStartBillingCheckoutMutation,
} from '../api/apiSlice';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageFallback';

interface ClubSettingsProps {
    clubId?: number;
}

export default function ClubSettings({ clubId }: ClubSettingsProps) {
    const { t } = useTranslation();
    const clubID = clubId != null ? String(clubId) : '';
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteLink, setInviteLink] = useState('');
    const [inviteError, setInviteError] = useState('');
    const [inviteEmailSent, setInviteEmailSent] = useState<boolean | null>(null);
    const [brief, setBrief] = useState('');
    const [briefError, setBriefError] = useState('');

    const { data: settings, isLoading } = useGetClubSettingsQuery(clubID, { skip: !clubID });
    const [updateClubLogo, { isLoading: isSaving }] = useUpdateClubLogoMutation();
    const [updateVisibility] = useUpdateClubVisibilityMutation();
    const [updateBrief, { isLoading: isSavingBrief }] = useUpdateClubBriefMutation();
    const [createInvite, { isLoading: isInviting }] = useCreateClubInviteMutation();
    const { data: billing } = useGetClubBillingQuery(clubId as number, { skip: clubId == null });
    const [startCheckout, { isLoading: isCheckingOut }] = useStartBillingCheckoutMutation();
    const [billingError, setBillingError] = useState('');

    const currentLogo = settings?.LOGO;

    useEffect(() => {
        if (!selectedFile) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(selectedFile);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [selectedFile]);

    useEffect(() => {
        setBrief(settings?.BRIEF ?? '');
    }, [settings?.BRIEF]);

    const handleSave = async () => {
        if (!selectedFile || !clubID) return;

        const formData = new FormData();
        formData.append('logo', selectedFile);

        try {
            await updateClubLogo({ clubId: Number(clubID), formData }).unwrap();
            setSelectedFile(null);
            setError('');
        } catch (err: any) {
            setError(err?.data?.message ?? t('common.genericError'));
        }
    };

    const handleVisibilityChange = async (isPublic: boolean) => {
        if (!clubId) return;

        try {
            await updateVisibility({ clubId, isPublic }).unwrap();
        } catch (err: any) {
            setError(err?.data?.message ?? t('common.genericError'));
        }
    };

    const handleSaveBrief = async () => {
        if (!clubId) return;

        setBriefError('');
        try {
            await updateBrief({ clubId, brief }).unwrap();
        } catch (err: any) {
            setBriefError(err?.data?.message ?? t('common.genericError'));
        }
    };

    const handleUpgrade = async (plan: 'club' | 'league') => {
        if (!clubId) return;

        setBillingError('');
        try {
            const checkoutUrl = await startCheckout({ clubId, plan }).unwrap();
            // Mollie-hosted payment page -- the webhook flips the plan once
            // the first payment completes, so nothing else to do here.
            window.location.href = checkoutUrl;
        } catch (err: any) {
            setBillingError(err?.data?.message ?? t('common.genericError'));
        }
    };

    const handleCreateInvite = async () => {
        if (!clubId || !inviteEmail) return;

        setInviteError('');
        setInviteLink('');
        setInviteEmailSent(null);

        try {
            const invite = await createInvite({ clubId, email: inviteEmail }).unwrap();
            // Private clubs never show up in the public directory, so the
            // signup page has no other way to know which club (or logo) to
            // display -- and prefilling the invited email saves the invitee
            // from having to guess the exact address the invite was tied to.
            // The backend already emails this link to the invitee; it's kept
            // here too as a fallback the admin can copy/paste (and the only
            // way to share it if sending failed).
            const params = new URLSearchParams({
                clubId: String(clubId),
                invite: invite.token,
                clubName: settings?.NAME ?? '',
                email: inviteEmail,
            });
            setInviteLink(`${window.location.origin}/signup?${params.toString()}`);
            setInviteEmailSent(invite.emailSent);
        } catch (err: any) {
            setInviteError(err?.data?.message ?? t('common.genericError'));
        }
    };

    return (
        <Box sx={{ maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
                {t('clubSettings.logoTitle')}
            </Typography>
            {isLoading ? (
                <CircularProgress />
            ) : (
                <Box
                    component="img"
                    src={previewUrl || currentLogo || PLACEHOLDER_IMAGE}
                    onError={handleImageError}
                    alt={t('clubSettings.logoTitle')}
                    sx={{ width: '100%', maxWidth: 240, display: 'block', mb: 2 }}
                />
            )}
            <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            />
            <Box display="flex" gap={1}>
                <Button variant="outlined" color="primary" onClick={() => fileInputRef.current?.click()}>
                    {t('clubSettings.chooseImage')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!selectedFile || isSaving}>
                    {t('common.save')}
                </Button>
            </Box>
            {error && (
                <Typography color="error" mt={1}>
                    {error}
                </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                {t('clubSettings.visibilityTitle')}
            </Typography>
            <FormControlLabel
                control={
                    <Switch
                        checked={settings?.IS_PUBLIC ?? true}
                        onChange={(e) => handleVisibilityChange(e.target.checked)}
                        disabled={!settings}
                    />
                }
                label={t('clubSettings.visibilityLabel')}
            />
            <Typography variant="body2" color="text.secondary">
                {settings?.IS_PUBLIC ? t('clubSettings.visibilityPublicHint') : t('clubSettings.visibilityPrivateHint')}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                {t('clubSettings.briefTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('clubSettings.briefHint')}
            </Typography>
            <TextField
                multiline
                minRows={2}
                fullWidth
                inputProps={{ maxLength: 300 }}
                placeholder={t('clubSettings.briefPlaceholder')}
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                helperText={`${brief.length}/300`}
            />
            {briefError && (
                <Typography color="error" mt={1}>
                    {briefError}
                </Typography>
            )}
            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 1 }}
                onClick={handleSaveBrief}
                disabled={isSavingBrief || brief === (settings?.BRIEF ?? '')}
            >
                {t('common.save')}
            </Button>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                {t('clubSettings.inviteTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('clubSettings.inviteHint')}
            </Typography>
            <Box display="flex" gap={1} mb={1}>
                <TextField
                    size="small"
                    type="email"
                    fullWidth
                    placeholder={t('clubSettings.inviteEmailPlaceholder')}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                />
                <Button variant="contained" onClick={handleCreateInvite} disabled={!inviteEmail || isInviting}>
                    {t('clubSettings.inviteGenerate')}
                </Button>
            </Box>
            {inviteError && <Typography color="error">{inviteError}</Typography>}
            {inviteEmailSent === true && (
                <Typography color="success.main" mb={1}>
                    {t('clubSettings.inviteSentMessage', { email: inviteEmail })}
                </Typography>
            )}
            {inviteEmailSent === false && (
                <Typography color="warning.main" mb={1}>
                    {t('clubSettings.inviteNotSentMessage')}
                </Typography>
            )}
            {inviteLink && (
                <TextField
                    size="small"
                    fullWidth
                    value={inviteLink}
                    InputProps={{ readOnly: true }}
                    onFocus={(e) => e.target.select()}
                    helperText={t('clubSettings.inviteLinkHint')}
                />
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                {t('clubSettings.billingTitle')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2">{t('clubSettings.currentPlan')}</Typography>
                <Chip size="small" variant="outlined" label={billing?.PLAN ?? 'starter'} />
                {billing?.STATUS && (
                    <Chip
                        size="small"
                        color={billing.STATUS === 'active' ? 'success' : billing.STATUS === 'pending' || billing.STATUS === 'past_due' ? 'warning' : 'error'}
                        label={t(`clubSettings.billingStatus.${billing.STATUS}`, { defaultValue: billing.STATUS })}
                    />
                )}
            </Box>
            {billing?.STATUS === 'past_due' && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {t('clubSettings.billingPastDueHint')}
                </Alert>
            )}
            {(billing?.PLAN ?? 'starter') === 'starter' && (
                <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('clubSettings.upgradeHint')}
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" onClick={() => handleUpgrade('club')} disabled={isCheckingOut}>
                            {t('clubSettings.upgradeToClub')}
                        </Button>
                        <Button variant="outlined" onClick={() => handleUpgrade('league')} disabled={isCheckingOut}>
                            {t('clubSettings.upgradeToLeague')}
                        </Button>
                    </Box>
                </>
            )}
            {billing?.PLAN === 'club' && (
                <Button variant="outlined" onClick={() => handleUpgrade('league')} disabled={isCheckingOut}>
                    {t('clubSettings.upgradeToLeague')}
                </Button>
            )}
            {billingError && (
                <Typography color="error" mt={1}>
                    {billingError}
                </Typography>
            )}
        </Box>
    );
}
